import { FC, useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Table from "../../../../components/common/Table/Table";
import CardTable from "../../../../components/common/CardTable";
import characterCountInit from "form-pattern/src/scripts/form-character-count";
import InfoIcon from "../../../../../public/assets/images/info-circle-small-solid.svg";
import uploadOutline from "../../../../../public/assets/images/uploadOutline.svg";
import downloadOutline from "../../../../../public/assets/images/downloadOutline.svg";
import { useDispatch } from "react-redux";
import {
  getDocumentsList,
  uploadDocument,
  downloadDocument,
} from "../../../../store/slices/Citizenship/viewDocumentSlice";
import { useAppSelector } from "../../../../store/hooks";
import Loader from "../../../../components/common/Loader";
import { uploadDocumentFields } from "../../../../config/viewDocumentsFields";
import { validateForm } from "../../../../utils/validationEngine";
import UploadErrorBanner from "./UploadErrorBanner";
import Notifications from "../../../../components/common/Notifications";
import { trackButtonClick, trackDocumentUpload } from '../../../../services/analytics';

type TableColumn = {
  key: string;
  label: string;
  className?: string;
};

const columns: TableColumn[] = [
  { key: "documentName", label: "Document name", className: "!max-w-[200px] !break-words" },
  { key: "contentDescription", label: "Document content description", className: "!max-w-[250px] !break-words" },
  { key: "createdTime", label: "Date added", className: "!whitespace-nowrap !min-w-[120px]" },
  {key: "lastUpdatedBy", label: "Uploaded by", className: "!whitespace-nowrap !min-w-[120px]" },
  { key: "employeeUploadFlag", label: "Employee upload Flag", className: "!whitespace-nowrap !min-w-[150px]" },
  { key: "action", label: "Action", className: "!whitespace-nowrap !min-w-[120px]" },
];

type TableRow = {
  documentName: string;
  contentDescription: string;
  createdTime: string;
  lastUpdatedBy: string;
  employeeUploadFlag: string;
  id: string;
};

const DownloadButton = ({ onClick }: { onClick?: () => void }) => (
  <button className="button -primary -small whitespace-nowrap flex items-center " onClick={onClick}>
    <img
      src={downloadOutline}
      alt="download icon"
      className="w-6! h-6! flex-shrink-0"
      aria-hidden="true"
    />
    <span>Download</span>
  </button>
);

const MyDocuments: FC = () => {
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [downloadError, setDownloadError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [contentDescription, setContentDescription] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");
  const [fileFormatError, setFileFormatError] = useState<string>("");
  const [fileSizeError, setFileSizeError] = useState<string>("");
  const [noFileSelectedError, setNoFileSelectedError] = useState<string>("");
  const { id } = useParams<{ id: string }>();
  const [documents, setDocuments] = useState<TableRow[]>([]);
  const { loading } = useAppSelector((state) => state.viewDocument);
  const dispatch = useDispatch();
  const location = useLocation();
  const createdDate = location.state?.createdDate;
    // Ref for success toast
  const successToastRef = useRef<HTMLDivElement>(null);
  const formatDate = (date: string) => {
    if (!date) return '';
    return date.replaceAll('-', '/');
  };


  // Scroll to success toast when it appears
  useEffect(() => {
    if (showSuccessToast && successToastRef.current) {
      successToastRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [showSuccessToast]);
  const fetchDocuments = async () => {
    if (!id) {
      console.error("No application id provided.");
      setDocuments([]);
      return;
    }
    try {
      const action = await dispatch<any>(
        getDocumentsList({ applicationId: id })
      );
      if (getDocumentsList.fulfilled.match(action)) {
  const payload = action.payload;

  if (payload?.items) {
    setDocuments(payload.items);
  } else {
    setDocuments([]);
  }
} else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      setDocuments([]);
    }
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        characterCountInit();
        console.log("Character count initialized");
      } catch (error) {
        console.error("Character count init error:", error);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    fetchDocuments();
  }, [id]);

  // Auto-dismiss download error after 10 seconds
  useEffect(() => {
    if (!downloadError) return;
    
    const timer = setTimeout(() => {
      setDownloadError("");
    }, 10000);

    return () => clearTimeout(timer);
  }, [downloadError]);

 
  const handleChooseFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const formData = {
        documentFile: file.name,
        documentFileSize: file.size,
        contentDescription: contentDescription,
      };
      const validationResult = validateForm(uploadDocumentFields, formData);
      if (validationResult.errors['documentFile']) {
        setFileFormatError(validationResult.errors['documentFile']);
        setFileSizeError("");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

     
      if (validationResult.errors['documentFileSize']) {
        setFileSizeError(validationResult.errors['documentFileSize']);
        setFileFormatError("");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      
      setFileFormatError("");
      setFileSizeError("");
      setNoFileSelectedError(""); // Clear no file selected error when file is selected
      setSelectedFile(file);
      console.log("Selected file:", file.name);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setContentDescription("");
    setValidationError("");
    setFileFormatError("");
    setFileSizeError("");
    setNoFileSelectedError("");
    const textarea = document.getElementById("text-area-6") as HTMLTextAreaElement;
    if (textarea) {
      textarea.value = "";
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContentDescription(e.target.value);
    if (validationError && e.target.value.trim()) {
      setValidationError("");
    }
    // Clear no file selected error when user starts typing
    if (noFileSelectedError && e.target.value.trim()) {
      setNoFileSelectedError("");
    }
  };


  const handleUpload = async () => {
   
    if (!selectedFile) {
      setNoFileSelectedError("Please choose a document to upload.");
      setFileFormatError("");
      setFileSizeError("");
      setValidationError("");
      return;
    }

    // Clear no file selected error
    setNoFileSelectedError("");

    const fileFormatData = {
      documentFile: selectedFile.name,
      documentFileSize: 0, 
      contentDescription: "", 
    };
    
    const fileFormatValidation = validateForm(
      uploadDocumentFields.filter(field => field.name === 'documentFile'), 
      fileFormatData
    );

    if (!fileFormatValidation.isValid && fileFormatValidation.errors['documentFile']) {
      setFileFormatError(fileFormatValidation.errors['documentFile']);
      setFileSizeError("");
      setSelectedFile(null); 
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    setFileFormatError("");
    const fileSizeData = {
      documentFile: selectedFile.name,
      documentFileSize: selectedFile.size,
      contentDescription: "", 
    };
    
    const fileSizeValidation = validateForm(
      uploadDocumentFields.filter(field => field.name === 'documentFileSize'), 
      fileSizeData
    );

    if (!fileSizeValidation.isValid && fileSizeValidation.errors['documentFileSize']) {
      setFileSizeError(fileSizeValidation.errors['documentFileSize']);
      setSelectedFile(null); 
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFileSizeError("");
    const fullFormData = {
      documentFile: selectedFile.name,
      documentFileSize: selectedFile.size,
      contentDescription: contentDescription,
    };

    const contentValidation = validateForm(uploadDocumentFields, fullFormData);

    if (!contentValidation.isValid) {
      if (contentValidation.requiredIfErrors?.['contentDescription']) {
        setValidationError(contentValidation.requiredIfErrors['contentDescription']);
        const textarea = document.getElementById("text-area-6");
        if (textarea) {
          textarea.scrollIntoView({ behavior: "smooth", block: "center" });
          textarea.focus();
        }
        return;
      }
      
    
      if (contentValidation.errors['contentDescription']) {
        setValidationError(contentValidation.errors['contentDescription']);
        const textarea = document.getElementById("text-area-6");
        if (textarea) {
          textarea.scrollIntoView({ behavior: "smooth", block: "center" });
          textarea.focus();
        }
        return;
      }
    }

    setValidationError("");
    setFileFormatError("");
    setFileSizeError("");
    console.log("Uploading file:", selectedFile.name);
    console.log("Content description:", contentDescription);
    
  
    
    // Reset form after successful upload
    
    if (selectedFile && id) {
      const description =
        (document.getElementById("text-area-6") as HTMLTextAreaElement)?.value ||
        "";

      try {
        const action = await dispatch<any>(
          uploadDocument({
            applicationId: id,
            file: selectedFile,
            description,
          })
        );

        if (uploadDocument.fulfilled.match(action)) {
          console.log(`📊 Analytics: Document uploaded successfully - ${selectedFile.name}`);
          trackDocumentUpload('citizenship', selectedFile.name, true);
          trackButtonClick('Upload', 'my-documents', { fileName: selectedFile.name, applicationId: id });
          setShowSuccessToast(true);

          setTimeout(() => {setShowSuccessToast(false);}, 25000);
          setSelectedFile(null);
          setContentDescription("");
          const textArea = document.getElementById("text-area-6") as HTMLTextAreaElement;
          if (textArea) textArea.value = "";

        
          fetchDocuments();
        } else {
          console.error("Failed to upload document:", action.payload);
          trackDocumentUpload('citizenship', selectedFile.name, false, 'Upload failed');
        }
      } catch (error) {
        console.error("Error during document upload:", error);
      }
    } else {
      alert("Please select a file and ensure application ID is available.");
    }

  };

  
  const handleDownload = async (documentId: string, fileName?: string) => {
    if (!id) {
      console.error("No application id provided.");
      return;
    }

    // Clear any previous download error
    setDownloadError("");

    const action = await dispatch<any>(
      downloadDocument({ applicationId: id, documentId, fileName })
    );

    if (downloadDocument.fulfilled.match(action)) {
      console.log("Document downloaded successfully.");
    } else {
      console.error("Failed to download document:", action.payload);
      const errorMsg = action.payload as string || "Failed to download document";
      setDownloadError(errorMsg);
    }
  };
 
  return (
    <div className="margin-top-4u">
      {loading && <Loader />}
      {downloadError && (
        <div className="margin-top-4u lg:mx-[40px]!">
          <UploadErrorBanner
            header="Download failed"
            message={downloadError}
            className="w-[70%]"
          />
        </div>
      )}
      <div>
     <p className="p3 -book !mb-[24px]">
          On this page, you can <strong>manage documents associated with your application.</strong> You have two main actions:
        </p>
        <ul className="list-disc !ml-[40px]">
          <li>
            <p className="p3 -book !mb-[8px]">
              <strong>Download Document:</strong> Review the list of available documents and select the one you need. You can now download <strong>one file at a time</strong> in its original format.
            </p>
          </li>
          <li>
            <p className="p3 -book !mb-[24px]">
              <strong>Upload Documents:</strong> If you need to provide additional documentation, you can upload files using the section below. Make sure to follow the upload guidelines for file type and size.
        </p>
          </li>
        </ul>
        </div>
      <div className="margin-top-4u lg:mx-[40px]!">
        

        <CardTable
          className="!mb-[61px] px-4 mx-5 sm:mx-0 sm:px-0"
          aria-label="My documents table"
        >
          <h3 className="h3">My documents</h3>
          <div className="lg:mt-[20px]!">
            {documents.length > 0 ? (
              <>
                <p className="p3 -book mb-4!">
                  Documents associated with this application
                </p>
                <Table
                  columns={columns}
                  rows={documents.map((row) => ({
                    ...row,
                    action: (
                      <DownloadButton onClick={() => handleDownload(row.id,row.documentName)} />
                    ),
                  }))}
                />
              </>
            ) : (
              !loading && (
                <p className="p3 -book text-center mt-[10px]! text-red-600">
                  No documents found for this application.
                </p>
              )
            )}
          </div>
        </CardTable>
      </div>

      <hr className="-variation-1 !py-[30px]" />
      {/* Success Toast with scroll-into-view */}
      {showSuccessToast && (
        <div ref={successToastRef}>
          <Notifications 
            messageInfo="Document has been uploaded successfully." 
            status={true} 
          />
        </div>
      )}

      <h2 className="h2">Upload documents</h2>

      {/* Upload Section */}
      <div>
        <p className="p3 -book">
          upload documents for application submitted on {formatDate(createdDate)}
        </p>

        <h3 className="h3 margin-top-2u">How to upload:</h3>
        <p className="p3 -book">
          To upload a document, please follow the steps below.
        </p>

        {/* Instructions */}
        <ol className="mb-4">
          <li className="p3 -boook mt-6!"> Select the application.</li>
          <li className="p3">
            1. Click <b>Choose File</b> to select the document.
          </li>
          <li className="p3">2. Enter a content description.</li>
          <li className="p3">
            3. Click <b>Upload</b> to attach the document.
          </li>
        </ol>

        <ul className="list-disc pl-6 !mt-0 marker:text-[#D1480C] !ml-[14px]">
          <li className="!mt-6 text-[#D1480C] p3 -bbok !mb p3 -book">
            Required fields
          </li>
          <li className="p3 -bbok">
            Upload New Document (Upload only PDF, JPG, PNG, DOC, and DOCX files).
          </li>
          <li className="p3 -book">
            You must provide a description of the contents when uploading a
            document.
          </li>
          <li className="p3 -book">
            Clear button to clear the input fields before uploading.
          </li>
        </ul>

        {noFileSelectedError && (
          <UploadErrorBanner
            header="No file selected"
            message={noFileSelectedError}
            className="w-[70%]"
          />
        )}

        {fileFormatError && (
          <UploadErrorBanner
            header="Incorrect file type"
            message="The selected file type is not supported. Please upload a PDF, JPG, PNG, DOC, or DOCX file."
            className="w-[70%]"
          />
        )}

        {fileSizeError && (
          <UploadErrorBanner
            header="File size exceeds limit"
            message="The selected file is too large. Please upload a file that is 20 MB or smaller."
            className="w-[70%]"
          />
        )}

        <div className="inline-flex items-center gap-2 border border-gray-300 bg-[#F7F7F7] text-[#878F9B] !p-4 !mt-[20px]">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            data-testid="file-input"
          />

          <button className="button whitespace-nowrap flex items-center gap-2 flex-shrink-0" onClick={handleChooseFile}>
            <img
              src={uploadOutline}
              alt="upload icon"
              className="!w-[1.6rem] !h-[1.25rem] flex-shrink-0"
              aria-hidden="true"
            />
            <span>Choose File</span>
          </button>

          {fileFormatError ? (
            <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
              <img 
                src="/assets/images/errorCircleSmall.svg" 
                alt="Error icon"
                className="!w-[16px] !h-[16px]"
              />
              {fileFormatError}
            </p>
          ) : fileSizeError ? (
            <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
              <img 
                src="/assets/images/errorCircleSmall.svg" 
                alt="Error icon"
                className="!w-[16px] !h-[16px]"
              />
              {fileSizeError}
            </p>
          ) : (
            <p className="p3 -dolphin !mt-0">Max file size 20 MB</p>
          )}
        </div>

        {selectedFile && (
          <div className="mt-2">
            <span>Selected file: {selectedFile.name}</span>
          </div>
        )}
      </div>

      {validationError && (
        <UploadErrorBanner
          header="Upload document comment is missing"
          message="Please add a comment for the uploaded document before continuing."
          className="w-[70%]"
        />
      )}

      <div className="form-group margin-top-2u">
        <label
          id="textarea-label2"
          className="p4 -medium after:content-['*'] after:text-[#D1480C]"
          htmlFor="text-area-6"
          aria-required={true}
        >
          Describe the contents
        </label>
        <div className="textarea-wrapper">
          <textarea
            autoComplete="on"
            id="text-area-6"
            aria-labelledby="textarea-label2 textarea_count2"
            aria-describedby="textarea-error-description-list-2"
            className={`text-area_word-count !text-[#0D1C3D] text-area_global -book ${validationError && !contentDescription.trim() ? 'field-error' : ''}`}
            data-show-lines="5"
            maxLength={1000}
            onChange={handleContentChange}
            value={contentDescription}
          ></textarea>
          {!(validationError && !contentDescription.trim()) && (
  
          <div className="character-count">
              <span
                id="textarea_count2"
                aria-live="polite"
                aria-atomic="true"
                className="count-area text-input-helper"
                data-description=" of 1000 characters left"
                data-full-text="No more characters available, 1000 of 1000"
              >
               {contentDescription.length === 0 ? '1000 of 1000 characters left' : `${1000 - contentDescription.length} of 1000 characters left`}
              </span>
            </div>
          )}
        </div>
        {validationError && !contentDescription.trim() && (
          <div className="error-description mt-2">
            <ul id="textarea-error-description-list-2">
              <li>
                <img src="/assets/images/errorCircleSmall.svg" alt="Error icon"/>
                <span className="screenreader-only">Error</span>
              {validationError}
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-4 margin-top-2u sm:flex-row">
        <button className="button -light" onClick={handleCancel}>
          Clear information
        </button>

        <button className="button -primary -light" onClick={handleUpload}>
          Upload document
        </button>
      </div>

      <div className="relative !mt-6 !mb-4 flex gap-2">
        <img src={InfoIcon} alt="info icon" />
        <p className="p3 -book">
          If you select clear button you may need to re-enter your description
          and re-upload documentation
        </p>
      </div>
    </div>
  );
};

export default MyDocuments;
